import logo from "../assets/img/om-full-primary-logo.svg";
import html2pdf from "html2pdf.js";
import React from "react";
import { createRoot } from "react-dom/client";
import { currencyFormatter } from "../util/util";
import { formatDateTime } from "../util/dateFormatter";
// PDF Template Component - all styles inline, no global effects
const ContributionScheduleTemplatePDF = ({ data }) => (
  <div
    style={{
      width: "100%",
      maxWidth: "210mm",
      padding: "10mm",
      margin: "0 auto",
      fontFamily: '"Times New Roman", Times, serif',
      fontSize: "12px",
      lineHeight: "1.4",
      color: "#000",
      backgroundColor: "#fff",
      boxSizing: "border-box",
    }}
  >
    {/* Logo aligned left */}
    <div
      style={{
        textAlign: "left",
        marginBottom: "8mm",
        pageBreakInside: "avoid",
      }}
    >
      <img
        src={logo}
        alt="Old Mutual Logo"
        style={{
          height: "10mm",
          maxWidth: "50mm",
          objectFit: "contain",
        }}
      />
    </div>

    {/* Title */}
    <div
      style={{
        marginBottom: "8mm",
        pageBreakInside: "avoid",
      }}
    >
      <h1
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          margin: 0,
          textTransform: "uppercase",
          textAlign: "center",
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        CONTRIBUTION SCHEDULE FORM
      </h1>
    </div>

    {data.errors && Array.isArray(data.errors) && data.errors.length > 0 && (
      <div
        style={{
          marginBottom: "8mm",
          pageBreakInside: "avoid",
        }}
      >
        <h3
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "5mm",
            color: "#d32f2f",
            fontFamily: '"Times New Roman", Times, serif',
            textAlign: "left",
          }}
        >
          ERROR DETAILS:
        </h3>
        <div
          style={{
            border: "1px solid #d32f2f",
            borderRadius: "4px",
            padding: "10px",
            backgroundColor: "#fff5f5",
            maxHeight: "200px",
            overflowY: "auto",
            fontSize: "12px",
            fontFamily: '"Times New Roman", Times, serif',
          }}
        >
          {data.errors.map((error, i) => (
            <div key={i} style={{ marginBottom: "8px" }}>
              <strong>Staff Number:</strong> {error.staffNumber}
              <br />
              <span style={{ color: "#d32f2f" }}>{error.errorMessage}</span>
            </div>
          ))}
        </div>
        <hr style={{ border: "1px solid #000", margin: "10px 0" }} />
      </div>
    )}

    {/* Info Section */}
    <div
      style={{
        marginBottom: "20px",
        width: "100%",
        paddingRight: "10px", // less padding to pull left
        maxWidth: "95%", // optional limit on max width
      }}
    >
      {[
        {
          label: "Scheme:",
          value: data.schemeType || "",
        },
        {
          label: "Record Number:",
          value: data.recordNumber || "",
        },
        {
          label: "Pension Type:",
          value: data.pensionType || "",
        },
        {
          label: "Branch:",
          value: data.branch || "",
        },
        {
          label: "Date:",
          value: data.requestDate
            ? new Date(data.requestDate)
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
                .toUpperCase()
            : new Date()
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
                .toUpperCase(),
        },
      ].map(({ label, value }, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            justifyContent: "flex-end", // force right align
            marginBottom: idx === 4 ? "0" : "6px",
            width: "100%",
          }}
        >
          <div
            style={{
              fontSize: "12px", // fixed font size
              fontWeight: "bold",
              textAlign: "right", // right aligned text
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "90%",
              textTransform: "uppercase",
              color: "#000",
              fontFamily: '"Times New Roman", Times, serif',
            }}
            title={value}
          >
            {label.toUpperCase()}{" "}
            <span style={{ fontWeight: "bold" }}>{value.toUpperCase()}</span>
          </div>
        </div>
      ))}
    </div>

    <hr style={{ border: "1px solid #000", margin: "10px 0" }} />

    {/* Section A: Schedule Details */}
    <div style={{ marginBottom: "20px" }}>
      <h3
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          marginBottom: "10px",
          fontFamily: '"Times New Roman", Times, serif',
          textAlign: "left",
        }}
      >
        A. SCHEDULE DETAILS.
      </h3>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: "16px",
        }}
      >
        <tbody>
          {/* Row 1 - Full width left-aligned (single cell) */}
          <tr>
            <td
              colSpan={2}
              style={{
                paddingBottom: "5px",
                textAlign: "left",
              }}
            >
              Employer Name:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.employerName || ""}
              </span>
            </td>
          </tr>

          {/* Row 2 - Left + Right */}
          <tr>
            <td style={{ paddingBottom: "5px", textAlign: "left" }}>
              Employer Member No.:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.employerNumber || ""}
              </span>
            </td>
            <td style={{ paddingBottom: "5px", textAlign: "right" }}>
              Payment Date:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.paymentDate
                  ? new Date(data.paymentDate).toISOString().split("T")[0]
                  : ""}
              </span>
            </td>
          </tr>

          {/* Row 3 */}
          <tr>
            <td style={{ paddingBottom: "5px", textAlign: "left" }}>
              Contribution Month:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.contributionMonth
                  ? String(data.contributionMonth).padStart(2, "0")
                  : ""}
              </span>
            </td>
            <td style={{ paddingBottom: "5px", textAlign: "right" }}>
              Contribution Year:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.contributionYear || ""}
              </span>
            </td>
          </tr>

          {/* Row 4 */}
          <tr>
            <td style={{ paddingBottom: "15px", textAlign: "left" }}>
              Total No Of Employees:{" "}
              <span style={{ fontWeight: "bold" }}>
                {data.totalEmployee || ""}
              </span>
            </td>
            <td style={{ paddingBottom: "15px", textAlign: "right" }}>
              Amount Due:GHS{" "}
              <span style={{ fontWeight: "bold" }}>{data.amountDue || ""}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        style={{
          fontSize: "16px",
          marginBottom: "15px",
          textAlign: "justify",
          lineHeight: 1.3,
          fontWeight: "bold",
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        We wish to acknowledge receipt of your contribution schedule for
        validation. Kindly proceed to make payment of{" "}
        <strong
          style={{
            fontSize: "16px", // ensure same size
            fontFamily: '"Times New Roman", Times, serif', // match parent
          }}
        >
          (GHS {data.amountDue || ""})
        </strong>{" "}
        into the custodian account.
      </p>
    </div>

    {/* Section B: Form Completed By */}
    <div style={{ marginBottom: "20px" }}>
      <h3
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          marginBottom: "10px",
          textAlign: "center",
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        B. FORM COMPLETED BY.
      </h3>
      <br />
      <hr style={{ border: "1px solid #000", margin: "10px 0" }} />
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: "16px",
        }}
      >
        <tbody>
          <tr>
            {/* Name */}
            <td style={{ paddingBottom: "5px", whiteSpace: "nowrap" }}>
              Name:
            </td>
            <td
              style={{
                paddingBottom: "5px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              {data.contactPerson || ""}
            </td>

            {/* Spacer between Name and Designation */}
            <td style={{ width: "40px" }}></td>

            {/* Designation */}
            <td style={{ paddingBottom: "5px", whiteSpace: "nowrap" }}>
              Designation:
            </td>
            <td
              style={{
                paddingBottom: "5px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              {data.designation || ""}
            </td>
          </tr>
        </tbody>
      </table>
      <br />
      <br />

      <p
        style={{
          fontSize: "16px",
          marginBottom: "8px",
          textAlign: "center",
          lineHeight: 1.3,
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        <p>Declaration :</p>
      </p>

      <p
        style={{
          fontSize: "16px",
          marginBottom: "10px",
          textAlign: "justify",
          lineHeight: 1.3,
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        I , the undersigned , hereby declare that the information provided by me
        and required of me for this form is both correct and accurate.
      </p>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: "16px",
        }}
      >
        <tbody>
          <tr>
            <td
              style={{
                paddingBottom: "15px",
                textAlign: "right",
                whiteSpace: "nowrap",
                width: "auto",
                fontWeight: "normal", // label not bold
              }}
            >
              Date:&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>
                {data.requestDate ? formatDateTime(data.requestDate) : ""}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      style={{
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "bold",
        marginBottom: "20px",
        textTransform: "uppercase",
      }}
    >
      KINDLY PROCEED TO MAKE PAYMENT
    </div>

    {/* Page Break */}
    <div style={{ pageBreakBefore: "always" }}></div>

    {/* Dynamic Schedule Pages: chunk employees into pages */}
    {(() => {
      const rowsPerPage = 28; // adjust as needed for fit
      const employees = data.employees || [];
      if (!employees.length) {
        return (
          <div style={{ marginBottom: "20px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              SCHEDULE
            </h3>
            <hr style={{ border: "1px solid #000", margin: "10px 0" }} />
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                fontStyle: "italic",
              }}
            >
              No employee data available
            </div>
          </div>
        );
      }

      // chunk employees
      const pages = [];
      for (let i = 0; i < employees.length; i += rowsPerPage) {
        pages.push(employees.slice(i, i + rowsPerPage));
      }

      return pages.map((pageEmployees, pageIndex) => (
        <div
          key={`schedule-page-${pageIndex}`}
          style={{
            marginBottom: "20px",
            pageBreakAfter: pageIndex === pages.length - 1 ? "auto" : "always",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              marginBottom: "10px",
              textAlign: "left",
            }}
          >
            SCHEDULE
          </h3>
          <hr style={{ border: "1px solid #000", margin: "10px 0" }} />
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "9px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "left",
                    fontWeight: "bold",
                  }}
                >
                  NAME
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  STAFF ID
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  SALARY
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  5%
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  18%
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  SNNIT NO.
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  USER TYPE
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "4px 2px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  GHANA CARD
                </th>
              </tr>
            </thead>
            <tbody>
              {pageEmployees.map((employee, index) => {
                const fullName = [
                  employee.firstName,
                  employee.lastName,
                  employee.otherName,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <tr
                    key={`${pageIndex}-${index}`}
                    style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                  >
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "left",
                      }}
                    >
                      {fullName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "center",
                      }}
                    >
                      {employee.staffNumber || ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "right",
                      }}
                    >
                      {employee.salary
                        ? currencyFormatter(employee.salary)
                        : ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "right",
                      }}
                    >
                      {employee.fivePercent
                        ? currencyFormatter(employee.fivePercent)
                        : ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "right",
                      }}
                    >
                      {employee.eighteenPercent
                        ? currencyFormatter(employee.eighteenPercent)
                        : ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "center",
                      }}
                    >
                      {employee.ssnitNumber || ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "center",
                      }}
                    >
                      {employee.userType || "Old Member"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "3px 2px",
                        textAlign: "center",
                      }}
                    >
                      {employee.ghanaNumber || ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ));
    })()}
  </div>
);

// Component to use with html2pdf
const ValidationReportPDF = ({ apiResponse }) => {
  const processedData = processApiResponse(apiResponse);
  return <ContributionScheduleTemplatePDF data={processedData} />;
};

// Helper function to process API response
const processApiResponse = (apiResponse) => {
  if (!apiResponse) return {};

  // Handle both direct response and nested data structure
  const rootData = apiResponse.data || apiResponse;
  const requestData = apiResponse.requestData || {};
  const errors =
    rootData.errors &&
    Array.isArray(rootData.errors) &&
    rootData.errors.length > 0
      ? rootData.errors
      : null;
  // Combine data from both response and request
  return {
    ...rootData,
    ...requestData,
    errors: errors,
    requestDate:
      rootData.requestDate ||
      apiResponse.requestDate ||
      new Date().toISOString(),
    employerName: rootData.employerName || requestData.employerName || "",
    employerNumber: rootData.employerNumber || requestData.employerNumber || "",
    contributionMonth:
      rootData.contributionMonth || requestData.fundMonth || "",
    contributionYear: rootData.contributionYear || requestData.fundYear || "",
    totalEmployee:
      rootData.totalEmployee ||
      (requestData.members ? requestData.members.length : 0),
    paymentDate: rootData.paymentDate || "",
    amountDue:
      rootData.amountDue ||
      requestData.totalFivePercent + requestData.totalEighteenPercent ||
      "",
    contactPerson: rootData.contactPerson || "",
    designation: rootData.designation || "",
    recordNumber: rootData.recordNumber || "",
    pensionType: rootData.pensionType || "",
    branch: rootData.branch || "Head Office",
    scheme: rootData.scheme || "ANCHOR TIER 2 MASTER TRUST SCHEME",
    employees: rootData.members || requestData.members || [],
  };
};

// Standalone download function using html2pdf
export const downloadValidationReport = (apiResponse) => {
  if (!apiResponse) {
    console.error("No API response provided");
    return;
  }

  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  // make container large enough to hold all pages to allow html2pdf to paginate
  const employees =
    (apiResponse &&
      (apiResponse.data?.members ||
        apiResponse.members ||
        apiResponse.data?.members)) ||
    [];
  container.style.width = "210mm";
  container.style.minHeight = `${Math.max(1, employees.length / 1) * 297}mm`;
  document.body.appendChild(container);

  const root = createRoot(container);

  root.render(
    <div id="pdf-content">
      <ValidationReportPDF apiResponse={apiResponse} />
    </div>,
  );

  setTimeout(() => {
    const element = document.getElementById("pdf-content");
    if (element) {
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Validation_Report_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 1.25,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          hotfixes: ["px_scaling"],
          putOnlyUsedFonts: true,
          compress: true,
        },
        pagebreak: {
          mode: ["css", "legacy"],
          avoid: ["tr", ".avoid-break", "table"],
          before: ".page-break-before",
          after: ".page-break-after",
        },
      };

      html2pdf().from(element).set(opt).save();
    }

    setTimeout(() => {
      root.unmount();
      document.body.removeChild(container);
    }, 1000);
  }, 100);
};

// Custom hook for PDF generation
export const useValidationReportPDF = () => {
  return downloadValidationReport;
};
